import { useMemo, useRef, useState } from "react";
import { Modal, Row, Col, Button, Form } from "react-bootstrap";
import { AutoCol, LoadingSpinner, StatefulForm, useAutoSaveForm } from "../../component-library";
import { usePermissions } from "../../contexts";
import { Claims, FormSlugs, TissueOffer } from "../../models";
import {
    useAppSettingsQuery,
    useFormDataQuery,
    useSurgeryQuery,
    useSurgerySchedulerSurgeonQuery,
    useTissueOfferQuery,
    useTissueOfferStatusQuery,
    useTissueQuery,
} from "../../queries";
import { createPatch } from "rfc6902";
import { ReadonlyFormModal, TissueInformationForm, useDownloadForm } from "../forms";
import logo from "../../images/as-logo.png";
import { config } from "../../config";
import { Link } from "react-router-dom";

interface TissueOfferModalProps {
    show: boolean;
    onHide: () => void;
    tissueOffer: TissueOffer | undefined;
}

export function TissueOfferModal({ show, onHide, tissueOffer }: Readonly<TissueOfferModalProps>) {
    const { isSurgeryUser, hasPermission } = usePermissions();
    const {
        mySurgeonsQuery: { data: mySurgeons, isFetching: isMySurgeonsFetching },
    } = useSurgerySchedulerSurgeonQuery({ queryType: "mySurgeons" });
    const {
        detailQuery: { data: surgery, isFetching: isSurgeryFetching },
    } = useSurgeryQuery({ id: tissueOffer?.surgeryId });
    const {
        listQuery: { data: offerStatuses, isFetching: isOfferStatusesFetching },
    } = useTissueOfferStatusQuery();
    const { updateMutation } = useTissueOfferQuery({ id: tissueOffer?.id });
    const [responseType, setResponseType] = useState<"" | "accept" | "decline">("");
    const [declineReason, setDeclineReason] = useState<string>("");
    const [showTifModal, setShowTifModal] = useState(false);
    const acceptStatus = offerStatuses?.find((status) => status.name === "Accepted");
    const declineStatus = offerStatuses?.find((status) => status.name === "Declined");
    const canCreateOffer = hasPermission(Claims.OfferTissue);

    const {
        detailQuery: { data: tissue, isFetching: isTissueFetching },
        tissueSummaryQuery: { data: tissueSummary, isFetching: isTissueSummaryFetching },
    } = useTissueQuery({
        includeDetails: true,
        id: tissueOffer?.tissueId,
    });

    const {
        listQuery: { data: forms, isFetching: isFormsFetching },
    } = useFormDataQuery({ caseId: tissue?.caseId, tissueId: tissue?.id, formSlug: FormSlugs.TIF, getOrCreate: true });
    const formData = forms?.[0];

    const formState = useAutoSaveForm({ formDataId: formData?.id, isReadonlyModal: true });

    const { methods, refetchFormData } = formState;

    const isLoading =
        isTissueFetching ||
        isTissueSummaryFetching ||
        isMySurgeonsFetching ||
        isSurgeryFetching ||
        isOfferStatusesFetching ||
        isFormsFetching;

    const isMySurgeon = mySurgeons?.some((surgeon) => surgeon?.id === surgery?.surgeonId);
    const canAcceptOffer = (isSurgeryUser && isMySurgeon) || canCreateOffer; // ASN can accept on behalf of surgeon

    const updateOffer = async () => {
        if (!tissueOffer || !responseType || !canAcceptOffer || (responseType === "decline" && !declineReason)) return;
        const isAccepting = responseType === "accept";
        const updatedOffer: TissueOffer = {
            ...tissueOffer,
            tissueOfferStatusId: Number(isAccepting ? acceptStatus?.id : declineStatus?.id),
            declineReason,
            acceptedOn: isAccepting ? new Date().toISOString() : undefined,
        };
        const patch = createPatch(tissueOffer, updatedOffer);
        await updateMutation.mutateAsync(patch);
        onHide();
    };

    const handleClose = () => {
        setResponseType("");
        onHide();
    };

    const formRef = useRef(null);
    const {
        myAppSettingsQuery: { data: appSettings },
    } = useAppSettingsQuery();

    const { downloadPdf } = useDownloadForm({
        baseFileName: `${tissue?.number}_tif`,
        formRef,
        formData,
        refetchFormData,
    });

    const logoSrc = useMemo<string>(() => {
        return appSettings?.logoUri?.length ? appSettings?.logoUri : logo;
    }, [appSettings]);

    return (
        <>
            <Modal show={show} onHide={handleClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Tissue Offer</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {isLoading && <LoadingSpinner />}
                    {!isLoading && (
                        <>
                            <Row>
                                <Col>
                                    <div>
                                        Tissue:{" "}
                                        {isSurgeryUser ? (
                                            tissue?.number
                                        ) : (
                                            <Link to={`/lists/none/tissues/${tissue?.id}/placement`}>
                                                {tissue?.number}
                                            </Link>
                                        )}
                                    </div>
                                    <ul>
                                        <li>Aged: {tissueSummary?.daysOut} days</li>
                                        <li>Donor Age: {tissueSummary?.donorAge}</li>
                                        <li>Cell Count: {tissueSummary?.cellCount}</li>
                                        <li>Rim: {tissueSummary?.rim}</li>
                                        <li>Clear Zone: {tissueSummary?.clearZone}</li>
                                        <li>Endo Clear Zone: {tissueSummary?.endoClearZone}</li>
                                    </ul>
                                </Col>
                                <AutoCol>
                                    <Button onClick={() => setShowTifModal(true)}>View TIF</Button>
                                </AutoCol>
                            </Row>
                            {canAcceptOffer && (
                                <>
                                    <Form.Group controlId="responseType" className="mb-2">
                                        <Form.Label>Respond to Offer</Form.Label>
                                        <Form.Select
                                            value={responseType}
                                            onChange={(e) => setResponseType(e.target.value as "accept" | "decline")}
                                        >
                                            <option value="" disabled>
                                                Select a response...
                                            </option>
                                            <option value="accept">Accept</option>
                                            <option value="decline">Decline</option>
                                        </Form.Select>
                                    </Form.Group>
                                    {responseType === "decline" && (
                                        <Form.Group controlId="declineReason">
                                            <Form.Label>Decline Reason</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                value={declineReason}
                                                onChange={(e) => setDeclineReason(e.target.value)}
                                            />
                                        </Form.Group>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="secondary" onClick={handleClose}>
                        Close
                    </Button>
                    {canAcceptOffer && (
                        <Button
                            variant="primary"
                            onClick={updateOffer}
                            disabled={!responseType || (responseType === "decline" && !declineReason)}
                        >
                            Submit
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {showTifModal && (
                <ReadonlyFormModal
                    show={true}
                    onHide={() => setShowTifModal(false)}
                    title="Tissue Information Form"
                    onDownload={downloadPdf}
                >
                    {formData ? (
                        <StatefulForm methods={methods} onSubmit={() => {}} readonly={true}>
                            <div ref={formRef}>
                                <img
                                    alt={`${config.APP_NAME} logo`}
                                    src={logoSrc}
                                    style={{ maxWidth: "210px", maxHeight: "29px" }}
                                    className="print-only ms-auto"
                                />
                                <TissueInformationForm tissueId={tissue?.id} />
                            </div>
                        </StatefulForm>
                    ) : (
                        <LoadingSpinner />
                    )}
                </ReadonlyFormModal>
            )}
        </>
    );
}
